import { getUpgradeCurrentReplaceValueById } from "@/game/utils/upgrades";
import { type TileIndex, type TileNeighbour } from "@/types/tile";
import { ceilNumber } from "@/utils/number";
import { FrameName, tilerepoShipyardFrameNames } from "../config";
import { getStorageResourceMultiplier } from "../config/buildings";
import { type ResourceType } from "../config/resources";
import { UpgradeIds } from "../config/upgrades";
import TileEntity from "../TileEntity";

class PortBuildingTileEntity extends TileEntity {
	public static readonly frameName = FrameName.PORT;

	public get range(): number | null {
		if (!super.range) return null;
		return (
			super.range +
			getUpgradeCurrentReplaceValueById(UpgradeIds.trade_cog)
		);
	}

	public get consumption(): Map<ResourceType, number> {
		const consumption = new Map<ResourceType, number>(super.consumption);
		if (!this.hasShipyardNeighbours) {
			consumption.clear();
		}

		return consumption;
	}

	public get storage(): Map<ResourceType, number> {
		const storage = new Map<ResourceType, number>(super.storage);
		// Apply upgrades for each resource in the storage map.
		for (const resource of storage.keys()) {
			let resourceStorage = (storage.get(resource) as number) || 0;
			resourceStorage *=
				1 +
				getUpgradeCurrentReplaceValueById(
					UpgradeIds.efficient_storage2,
				) +
				getStorageResourceMultiplier(
					this.getOwnNeighbourTileEntitiesByNeighbourFrameName(
						FrameName.STORAGE_OVERSEER,
						1,
					),
				);
			resourceStorage = ceilNumber(resourceStorage);
			storage.set(resource, resourceStorage);
		}

		if (!this.hasShipyardNeighbours) {
			storage.clear();
		}

		return storage;
	}

	public get isConnectedToMainSettlement(): boolean {
		return this.hasShipyardNeighbours;
	}

	private get hasShipyardNeighbours(): boolean {
		const shipyardNeighbours = this.tilerepo
			.getNeighbourTileIndexesByTileCoordinates(
				{ x: this.x, y: this.y },
				1 + getUpgradeCurrentReplaceValueById(UpgradeIds.trade_cog),
			)
			.map((tileIndex: TileIndex): TileNeighbour | null => {
				const [, , tileId] =
					this.tilerepo.getTileIdsByTileIndex(tileIndex);
				if (!tileId) return null;

				const frameName = this.tilerepo.getFrameNameByTileId(tileId);
				if (!frameName) return null;

				return {
					tileIndex,
					tileId,
					frameName,
				};
			})
			.filter(
				(neighbour: TileNeighbour | null): boolean =>
					neighbour !== null &&
					tilerepoShipyardFrameNames.has(neighbour.frameName),
			) as Array<TileNeighbour>;

		if (!shipyardNeighbours.length) {
			return false;
		}

		return true;
	}
}

export default PortBuildingTileEntity;
